/**
 * menuItemSeeder - Declarative menu item registration for features
 *
 * Features register their menu items here. On app init, any missing
 * items are created in Firestore automatically. Existing items (by id)
 * are left untouched so admin customizations (reorder, hide) are preserved.
 *
 * To add a menu item for a new feature:
 *   1. Add an entry to FEATURE_MENU_ITEMS below
 *   2. That's it. It auto-seeds on next app load.
 */

import menuService from './menuService';

/**
 * ═══════════════════════════════════════════════════════
 * FEATURE MENU ITEMS REGISTRY
 *
 * Add new feature menu items here. All fields are explicit.
 * roles: { teacher, admin, parent, dev } — who sees it in the sidebar
 * ═══════════════════════════════════════════════════════
 */
export const FEATURE_MENU_ITEMS = [
  // Parent folder
  {
    id: 'organization',
    label: 'Organization',
    icon: 'BuildingOffice2Icon',
    href: '#',
    roles: { teacher: false, admin: true, parent: false, dev: true },
    order: 10.3,
    visible: true,
    showInMenu: true,
    parentId: null,
    depth: 0,
  },
  // Children
  {
    id: 'roles',
    label: 'Role Types',
    icon: 'BriefcaseIcon',
    href: '/roles',
    roles: { teacher: false, admin: true, parent: false, dev: true },
    order: 1,
    visible: true,
    showInMenu: true,
    parentId: 'organization',
    depth: 1,
  },
  {
    id: 'teams',
    label: 'Teams',
    icon: 'UsersIcon',
    href: '/teams',
    roles: { teacher: false, admin: true, parent: false, dev: true },
    order: 2,
    visible: true,
    showInMenu: true,
    parentId: 'organization',
    depth: 1,
  },
  {
    id: 'org-chart',
    label: 'Org Chart',
    icon: 'ShareIcon',
    href: '/org-chart',
    roles: { teacher: false, admin: true, parent: false, dev: true },
    order: 3,
    visible: true,
    showInMenu: true,
    parentId: 'organization',
    depth: 1,
  },
  // Workflows parent folder (dev-only)
  {
    id: 'workflows-group',
    label: 'Workflows',
    icon: 'ArrowPathIcon',
    href: '#',
    roles: { teacher: false, admin: false, parent: false, dev: true },
    order: 10.4,
    visible: true,
    showInMenu: true,
    parentId: null,
    depth: 0,
  },
  {
    id: 'workflowtemplates_1771520651641',
    label: 'Templates',
    icon: 'Cog6ToothIcon',
    href: '/workflow-templates',
    roles: { teacher: false, admin: false, parent: false, dev: true },
    order: 1,
    visible: true,
    showInMenu: true,
    parentId: 'workflows-group',
    depth: 1,
  },
  {
    id: 'workflows_1771520652083',
    label: 'Active',
    icon: 'ArrowPathIcon',
    href: '/workflows',
    roles: { teacher: false, admin: false, parent: false, dev: true },
    order: 2,
    visible: true,
    showInMenu: true,
    parentId: 'workflows-group',
    depth: 1,
  },
  // Grades — under School parent
  {
    id: 'grades',
    label: 'Grades',
    icon: 'AcademicCapIcon',
    href: '/grades',
    roles: { teacher: false, admin: true, parent: false, dev: true },
    order: 30,
    visible: true,
    showInMenu: true,
    parentId: 'school_1757962663554',
    depth: 1,
  },
  // Messenger
  {
    id: 'conversations',
    label: 'Messages',
    icon: 'ChatBubbleLeftRightIcon',
    href: '/conversations',
    roles: { teacher: true, admin: true, parent: false, dev: true },
    order: 3.5,
    visible: true,
    showInMenu: true,
    parentId: null,
    depth: 0,
  },
  // Class display — teacher workflow (per-class projector view).
  // Hidden for admin/parent (admins don't teach a class — they can't use it).
  {
    id: 'class-display',
    label: 'Class Display',
    icon: 'TvIcon',
    href: '/class-display',
    roles: { teacher: true, admin: false, parent: false, dev: true },
    order: 100,
    visible: true,
    showInMenu: true,
    parentId: null,
    depth: 0,
  },
  // Records — school-wide event log for the active domain (discipline when none
  // active). Sits in the discipline cluster, right after the Queue (order 9), so
  // the log lives beside the tools that create the events. Label stays neutral:
  // the menu is global and not relabelled per domain, while the page itself
  // retitles to the active domain — same pattern as "Quick Issue"/"Discipline
  // Queue". Same role gate as those (no parent).
  {
    id: 'records',
    label: 'Records',
    icon: 'RectangleStackIcon',
    href: '/records',
    roles: { teacher: true, admin: true, parent: false, dev: true },
    order: 9.5,
    visible: true,
    showInMenu: true,
    parentId: null,
    depth: 0,
  },
];

// Track whether we've already seeded this session (survives React Router remounts)
// Module-level _seeded resets on HMR; sessionStorage survives component tree remounts.
// Bump the version suffix whenever FEATURE_MENU_ITEMS gains a new entry so existing
// sessions force a re-seed pass and pick it up.
const SEEDER_VERSION = 'v3-records';
const SEEDED_KEY = `swan_menuItemSeeder_done_${SEEDER_VERSION}`;
let _seeded = false;

function isAlreadySeeded() {
  if (_seeded) return true;
  try {
    return sessionStorage.getItem(SEEDED_KEY) === 'true';
  } catch { return false; }
}

function markSeeded() {
  _seeded = true;
  try { sessionStorage.setItem(SEEDED_KEY, 'true'); } catch {}
}

/**
 * Ensure all FEATURE_MENU_ITEMS exist in Firestore.
 * Call this after menu items are loaded — pass the existing items
 * so we can diff without an extra Firestore read.
 *
 * @param {Array} existingItems - Current menu items from Firestore
 * @returns {Promise<{ created: string[], skipped: string[] } | null>}
 */
export async function ensureFeatureMenuItems(existingItems = []) {
  if (isAlreadySeeded()) {
    console.log('[menuItemSeeder] ⏭️ Already seeded this session, skipping');
    return null;
  }
  markSeeded();

  const existingMap = new Map(existingItems.map(i => [i.id, i]));
  const created = [];

  for (const item of FEATURE_MENU_ITEMS) {
    const existing = existingMap.get(item.id);

    if (!existing) {
      // Missing — create it
      try {
        await menuService.createMenuItem(item);
        created.push(item.id);
      } catch (err) {
        console.error(`[menuItemSeeder] Failed to create "${item.id}":`, err);
      }
    }
    // Existing items are left untouched — admin customizations are the source of truth
  }

  if (created.length > 0) console.log(`[menuItemSeeder] ✅ Created:`, created);
  else console.log('[menuItemSeeder] ✅ All menu items already exist, no writes needed');

  return { created };
}

/**
 * Standalone seed — fetches existing items itself. Use from console or scripts.
 * Usage: await seedMenuItems()
 */
export async function seedMenuItems(items = FEATURE_MENU_ITEMS) {
  _seeded = false; // Reset so it actually runs
  try { sessionStorage.removeItem(SEEDED_KEY); } catch {}
  const existing = await menuService.getAllMenuItems(null, true);
  return ensureFeatureMenuItems(existing);
}

export default seedMenuItems;
